import { useState } from "react";
import { createPortal } from "react-dom";
import { User, Mail, Tag, MessageSquare, Loader2, Send, X, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CustomerTypePicker, emptyCustomer, customerError, customerPayload } from "@/components/CustomerTypePicker";

export default function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [customer, setCustomer] = useState(emptyCustomer);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setForm({ name: "", email: "", subject: "", message: "" });
      setCustomer(emptyCustomer);
      setError("");
    }, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and message.");
      return;
    }
    if (customerError(customer)) {
      setError(customerError(customer));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        ...customerPayload(customer),
      });
      setSent(true);
      toast.success("Message sent — we'll be in touch!");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[130]" data-testid="contact-modal">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={close} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 my-4 animate-fade-up overflow-hidden">
            <div className="relative bg-slate-900 px-6 py-6 overflow-hidden">
              <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-indigo-600/30 blur-2xl" />
              <button onClick={close} data-testid="contact-modal-close-btn"
                className="absolute top-4 right-4 text-slate-400 hover:text-white transition">
                <X className="h-5 w-5" />
              </button>
              <div className="relative flex items-center gap-2 text-indigo-300 text-xs font-bold uppercase tracking-widest">
                <MessageSquare className="h-4 w-4" /> Contact Us
              </div>
              <h3 className="relative mt-2 font-head text-2xl font-extrabold text-white tracking-tight">Get in touch</h3>
              <p className="relative mt-1 text-sm text-slate-300">Questions about Smart-In? Send us a message and we'll reply by email.</p>
            </div>

            {sent ? (
              <div className="px-6 py-10 text-center" data-testid="contact-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h4 className="mt-4 font-head text-xl font-bold text-slate-900">Message sent</h4>
                <p className="mt-1 text-sm text-slate-500">Thanks for reaching out — our team will get back to you shortly.</p>
                <button onClick={close} data-testid="contact-done-btn"
                  className="mt-6 h-11 px-6 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition">
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 py-5 space-y-4" data-testid="contact-form">
                <CustomerTypePicker prefix="contact" value={customer} onChange={(c) => { setCustomer(c); setError(""); }} />
                <Field icon={User} label="Your Name" testid="contact-name" value={form.name} onChange={set("name")} placeholder="Full name" />
                <Field icon={Mail} label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                <Field icon={Tag} label="Subject (optional)" testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="How can we help?" />
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => set("message")(e.target.value)}
                    rows={4}
                    placeholder="Write your message…"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition resize-none"
                  />
                </div>

                {error && (
                  <p className="text-sm font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="contact-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="w-full h-12 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send Message <Send className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ icon: Icon, label, testid, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition"
        />
      </div>
    </div>
  );
}
